import type { ProjectDto } from '@/api'

import { reconcileStatus } from './server-status'
import type { ProjectRecord, WorkflowType } from './types'
import { WORKFLOW_TYPES } from './types'
import type { WorkflowProgressBody } from '@/api'

/** The backend's own words for what it accepts: PDF, PNG, JPG and IDML. */
export const OUTPUT_FORMAT_VALUES = ['PDF', 'PNG', 'JPG', 'IDML'] as const

/** The backend's words for the workflows. */
const SERVER_WORKFLOW: Record<WorkflowType, string> = { GENERATE_TEMPLATE: 'generate_template', INDESIGN_FILE: 'indesign_template', EXISTING_TEMPLATE: 'existing_template' }
export const toServerWorkflow = (workflow: WorkflowType) => SERVER_WORKFLOW[workflow]
export const fromServerWorkflow = (value: string | null | undefined): WorkflowType | null => {
  const word = (value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_')
  // `indesign_file` is what an earlier version of the backend called the InDesign workflow.
  const known = word === 'indesign_file' ? 'indesign_template' : word
  return WORKFLOW_TYPES.find((workflow) => SERVER_WORKFLOW[workflow] === known) ?? null
}

/** What every project update says besides the project's own fields: its SKU (so an update never clears it), the workflow itself, and the reviews once the server has told us them. */
export function workflowFields(project: Pick<ProjectRecord, 'workflowType' | 'workflowProgress'> & { sku?: string }): { sku?: string; workflowType?: string; workflowProgress?: WorkflowProgressBody } {
  return { ...(project.sku ? { sku: project.sku } : {}), ...(project.workflowType ? { workflowType: toServerWorkflow(project.workflowType) } : {}), ...(project.workflowProgress ? { workflowProgress: project.workflowProgress } : {}) }
}

/**
 * A project from the backend as the app holds it. The backend knows a project's name, client, brief, formats and size; it
 * does not know the workflow, the stage or the design work, which stay here. So when the project is already known here
 * those are kept as they are and only what the backend owns is refreshed; a project new to this browser starts at Details.
 * The status is the backend's: what it says now is the project's status (see `reconcileStatus` for how its four words meet the finer
 * ones kept here), so it is always the one to trust.
 *
 * The backend's `accountId` is the account of whoever created the project; the client it is for is its `assignedAccountId`. A project made
 * before that field existed has the client in `accountId`.
 */
export function toProject(api: ProjectDto, existing?: ProjectRecord): ProjectRecord {
  const outputFormats = api.outputFormats ?? []
  const owned = {
    id: api.id,
    clientAccountId: api.assignedAccountId ?? api.accountId,
    ownerAccountId: api.accountId,
    name: api.projectName ?? '',
    clientBrief: api.instructions ?? '',
    outputFormats,
    outputFormat: outputFormats.join(', '),
    pageSize: api.artworkSize ?? '',
    createdByUserId: api.createdBy,
    apiStatus: api.status ?? undefined,
    ...(api.sku?.trim() ? { sku: api.sku.trim() } : {}),
    generatedImagePaths: (api.generatedImageUrls ?? []).filter((path) => path.trim() !== ''),
    createdAt: api.createdAt,
    updatedAt: api.updatedAt ?? api.createdAt,
  }
  const progress = api.workflowProgress ?? undefined
  const workflowType = fromServerWorkflow(api.workflowType ?? progress?.workflowType)
  const reviews = progress ? { workflowProgress: { assetReview: progress.assetReview, artContent: progress.artContent, creativeBrief: progress.creativeBrief } } : {}
  if (existing) return { ...existing, ...owned, ...reviews, workflowType: workflowType ?? existing.workflowType, status: reconcileStatus(api.status, existing.status) }
  return {
    ...owned,
    description: '',
    ...reviews,
    workflowType,
    status: reconcileStatus(api.status, undefined),
    currentStage: 'DETAILS',
    assignedClientAt: null,
    masterTemplateId: null,
    latestVersionNumber: 0,
    approvalMode: null,
    candidate: null,
    businessType: '',
    location: '',
    preparation: null,
    lastItemNumber: 0,
  }
}
